import React, { useEffect, useId, useState } from 'react';
import { useIntl } from 'react-intl';
import type { KitAssetTrim } from 'fym-shared/src/music24/fullVideoPlan';
import { TypedMemo } from '../../../../common/types';
import { clockTime, fitTrim, parseClock } from '../fullVideoRules';
import { fullVideoMessages } from '../fullVideoMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from '../Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TimeFieldProps {
  label: string;
  valueSec: number;
  onCommit: (seconds: number) => void;
}

/** One time field ("1:05"). The value counts when the field loses focus or on Enter. */
const TimeFieldComponent: React.FC<TimeFieldProps> = ({ label, valueSec, onCommit }) => {
  const id = useId();
  const [text, setText] = useState(clockTime(valueSec));
  useEffect(() => setText(clockTime(valueSec)), [valueSec]);

  const commit = () => {
    const seconds = parseClock(text);
    if (seconds === null) {
      setText(clockTime(valueSec));
      return;
    }
    onCommit(seconds);
  };

  return (
    <label className={styles.trimField} htmlFor={id}>
      <span className={styles.mono}>{label}</span>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        className={styles.trimInput}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            commit();
          }
        }}
      />
    </label>
  );
};

const TimeField = TypedMemo(TimeFieldComponent);

interface TrimFieldsProps {
  durationSec: number;
  trim: KitAssetTrim;
  onChange: (trim: KitAssetTrim) => void;
}

/**
 * The two time fields of a trim (design s7): the start and the end of the part of the song. The
 * client has no beat grid, so a value snaps to whole seconds, inside the song, at least 30 s long;
 * the server snaps the trim to the downbeats.
 */
const TrimFieldsComponent: React.FC<TrimFieldsProps> = ({ durationSec, trim, onChange }) => {
  const intl = useIntl();
  return (
    <div className={styles.trim}>
      <TimeField
        label={intl.formatMessage(fullVideoMessages.trimStart)}
        valueSec={trim.startSec}
        onCommit={(startSec) => onChange(fitTrim({ ...trim, startSec }, durationSec, 'start'))}
      />
      <TimeField
        label={intl.formatMessage(fullVideoMessages.trimEnd)}
        valueSec={trim.endSec}
        onCommit={(endSec) => onChange(fitTrim({ ...trim, endSec }, durationSec, 'end'))}
      />
      <span className="small text-muted">
        {intl.formatMessage(fullVideoMessages.trimSummary, {
          from: clockTime(trim.startSec),
          to: clockTime(trim.endSec),
          length: clockTime(trim.endSec - trim.startSec),
        })}
      </span>
    </div>
  );
};

export const TrimFields = TypedMemo(TrimFieldsComponent);
